import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { metaApi } from "@/api";
import type { MetaAudience } from "@/types";
import { META_SYNC_STATUS_LABELS } from "@/types";
import { Target, Plus, RefreshCw, Settings, Trash2 } from "lucide-react";
import { format } from "date-fns";

const statusColors: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-slate-100 text-slate-600",
  ERROR: "bg-red-100 text-red-700",
};

export default function MetaAudiences() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["meta-audiences"],
    queryFn: () => metaApi.listAudiences(),
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => metaApi.syncAudience(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-audiences"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaApi.deleteAudience(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meta-audiences"] });
    },
  });

  const audiences = data?.data ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Target size={24} className="text-blue-500" />
          <h1 className="text-2xl font-bold">Meta広告オーディエンス</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/meta-settings")}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm border hover:bg-slate-50"
          >
            <Settings size={16} />
            設定
          </button>
          <button
            onClick={() => navigate("/meta-audiences/new")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
          >
            <Plus size={16} />
            新規オーディエンス
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : audiences.length === 0 ? (
        <div className="text-center py-16">
          <Target size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-4">オーディエンスがまだありません</p>
          <button
            onClick={() => navigate("/meta-audiences/new")}
            className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
          >
            最初のオーディエンスを作成
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">オーディエンス名</th>
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">ステータス</th>
                <th className="text-right text-xs font-medium text-slate-500 px-4 py-3">対象数</th>
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">最終同期</th>
                <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">直近変動</th>
                <th className="text-right text-xs font-medium text-slate-500 px-4 py-3">アクション</th>
              </tr>
            </thead>
            <tbody>
              {audiences.map((aud: MetaAudience) => {
                const lastLog = aud.syncLogs?.[0];
                return (
                  <tr
                    key={aud.id}
                    className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/meta-audiences/${aud.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{aud.name}</div>
                      {aud.metaAudienceId && (
                        <div className="text-xs text-slate-400">Meta ID: {aud.metaAudienceId}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[aud.syncStatus]}`}>
                        {META_SYNC_STATUS_LABELS[aud.syncStatus]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {aud.lastSyncCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {aud.lastSyncedAt
                        ? format(new Date(aud.lastSyncedAt), "MM/dd HH:mm")
                        : "未同期"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {lastLog ? (
                        <span className="text-xs text-slate-500">
                          +{lastLog.added} / -{lastLog.removed}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => syncMutation.mutate(aud.id)}
                          disabled={syncMutation.isPending}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-500 disabled:opacity-50"
                          title="今すぐ同期"
                        >
                          <RefreshCw size={14} className={syncMutation.isPending ? "animate-spin" : ""} />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("このオーディエンスを削除しますか？")) {
                              deleteMutation.mutate(aud.id);
                            }
                          }}
                          className="p-1.5 rounded hover:bg-red-50 text-red-400"
                          title="削除"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
